/**
 * Canonical catalog of result-unit options offered by numeric metric configs
 * (Custom Trace, Custom SQL, …). `metadataUnit` is written to
 * `metric_metadata.unit` and must be a value of the backend `ResultUnit` enum
 * (or `undefined` for "no unit"). Individual configs select the ordered subset
 * they support via {@link pickUnitOptions}, so the shared entries' labels and
 * `metadataUnit` values can't drift between configs.
 */

export interface UnitOption {
  key: string
  label: string
  metadataUnit: string | undefined
}

const UNIT_OPTION_CATALOG = {
  none: { key: "none", label: "Unitless", metadataUnit: undefined },
  s: { key: "s", label: "Seconds", metadataUnit: "s" },
  ms: { key: "ms", label: "Milliseconds", metadataUnit: "ms" },
  count: { key: "count", label: "Count", metadataUnit: "count" },
  ratio: { key: "ratio", label: "Ratio", metadataUnit: "ratio" },
  percent: { key: "percent", label: "Percent", metadataUnit: "percent" },
  per_minute: {
    key: "per_minute",
    label: "Per minute",
    metadataUnit: "per_minute",
  },
  per_second: {
    key: "per_second",
    label: "Per second",
    metadataUnit: "per_second",
  },
  per_message: {
    key: "per_message",
    label: "Per message",
    metadataUnit: "per_message",
  },
  tokens: { key: "tokens", label: "Tokens", metadataUnit: "tokens" },
  score: { key: "score", label: "Score", metadataUnit: "score" },
  severity: { key: "severity", label: "Severity", metadataUnit: "severity" },
  quality: { key: "quality", label: "Quality", metadataUnit: "quality" },
  dB: { key: "dB", label: "Decibels", metadataUnit: "dB" },
  LUFS: { key: "LUFS", label: "LUFS", metadataUnit: "LUFS" },
  Hz: { key: "Hz", label: "Hertz", metadataUnit: "Hz" },
  st: { key: "st", label: "Semitones", metadataUnit: "st" },
  // "Custom Text" stores no predefined unit; the trace config captures the free
  // text separately, so its metadataUnit is undefined like "none".
  custom: { key: "custom", label: "Custom Text", metadataUnit: undefined },
} as const satisfies Record<string, UnitOption>

type UnitOptionKey = keyof typeof UNIT_OPTION_CATALOG

/**
 * Returns the catalog entries for the given keys, in the order provided. The
 * generic preserves each option's literal `key`, so callers keep a precise key
 * union (e.g. for a config's unit-selector state type).
 */
export function pickUnitOptions<K extends UnitOptionKey>(
  keys: readonly K[],
): ReadonlyArray<(typeof UNIT_OPTION_CATALOG)[K]> {
  return keys.map((key) => UNIT_OPTION_CATALOG[key])
}
